import React, { useEffect, useMemo, useState } from "react";

import { MenuItem } from "@mui/material";
import { format as formatTz, utcToZonedTime } from "date-fns-tz";

import Input from "../../components/Generic/Input";
import InputDateRange from "../../components/Generic/Input/DateRange";
import Select from "../../components/Generic/Select";
import {
  DateOption,
  DateSelect,
  DefaultDateOptionsColumn,
  DefaultPeriodOptionsColumn,
} from "../../export";
import useStyles from "./styles";
import { SelectChangeEvent } from "@mui/material";

export enum RangeDateOptions {
  START_DATE = "startDate",
  END_DATE = "endDate",
}

const today = new Date();

const SelectDate = (props: DateSelect) => {
  const { dateOptions, periodOptions: overridePeriodOptions, startDate, endDate, onChange } = props;

  const classes = useStyles();

  const [filters, setFilters] = useState<{ [x: string]: string[] }>();

  const [dateOption, setDateOption] = useState<DateOption | undefined>(dateOptions?.[0]);

  // PERIOD VARIABLES
  const defaultPeriodOptions: DateOption[] = useMemo(
    () => [
      {
        name: "Custom period",
        column: DefaultPeriodOptionsColumn.CUSTOM_PERIOD,
        type: DefaultDateOptionsColumn.DATE_RANGE,
      },
      {
        name: "Custom date",
        column: DefaultPeriodOptionsColumn.CUSTOM_DATE,
        type: DefaultDateOptionsColumn.DATE,
      },
      {
        name: "Last day",
        column: DefaultPeriodOptionsColumn.LAST_DAY,
        type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      },
      {
        name: "Last week",
        column: DefaultPeriodOptionsColumn.LAST_WEEK,
        type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      },
      {
        name: "Last month",
        column: DefaultPeriodOptionsColumn.LAST_MONTH,
        type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      },
      {
        name: "Last 15 days",
        column: DefaultPeriodOptionsColumn.LAST_15_DAYS,
        type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      },
    ],
    []
  );

  const [periodOption, setPeriodOption] = useState<DateOption>(
    overridePeriodOptions?.[0] ?? defaultPeriodOptions[0]
  );

  const column = useMemo(
    () => (dateOption ? dateOption.column : periodOption.column),
    [dateOption]
  );

  useEffect(() => {
    if (onChange && filters) onChange(filters, dateOption?.column, periodOption.column);
  }, [filters, dateOption?.column, periodOption.column]);

  const rangeDates = useMemo(() => {
    if (filters && filters[column] && filters[column].length > 1) {
      return {
        [RangeDateOptions.START_DATE]: new Date(filters[column][0]),
        [RangeDateOptions.END_DATE]: new Date(filters[column][1]),
      };
    }

    if (filters && filters[column] && filters[column].length === 1) {
      return {
        [RangeDateOptions.START_DATE]: new Date(filters[column][0]),
      };
    }

    return {
      [RangeDateOptions.START_DATE]:
        startDate || new Date(today.getFullYear(), today.getMonth(), 1),
      [RangeDateOptions.END_DATE]: endDate || new Date(),
    };
  }, [filters]);

  const handleChangeDateOption = (value: string) => {
    if (dateOptions) {
      const currentOption = dateOptions.find((option) => option.column === value);

      if (currentOption?.type === DefaultDateOptionsColumn.HIDE_DATE_SELECTOR) {
        setPeriodOption({} as DateOption);
        setFilters({});
      } else if (periodOption.name === undefined) {
        setPeriodOption(overridePeriodOptions?.[0] ?? defaultPeriodOptions[0]);
      }
      setDateOption(currentOption ?? dateOptions[0]);
    }
  };

  const handleChangePeriodOption = (value: string) => {
    setFilters({});
    setPeriodOption(periodOptions.find((option) => option.column === value) ?? periodOptions[0]);
  };

  const handleClearDateRange = () => {
    setFilters((prev) => {
      const prevCopy = { ...prev };

      delete prevCopy?.[column];
      delete prevCopy?.[DefaultDateOptionsColumn.DATE_RANGE];

      return prevCopy;
    });
  };

  const formatInTimeZone = (fmt: string, tz: string, date?: string | number | Date) => {
    if (date) return formatTz(utcToZonedTime(date, tz), fmt, { timeZone: tz });

    return undefined;
  };

  const formatDate = (date: Date) => {
    return formatInTimeZone("yyyy-MM-dd", "UTC", date);
  };

  useEffect(() => {
    switch (periodOption.type) {
      case DefaultDateOptionsColumn.DATE:
        setFilters((prev) => ({
          ...prev,
          [DefaultDateOptionsColumn.DATE]: [formatDate(rangeDates?.startDate) ?? ""],
        }));
        break;

      case DefaultDateOptionsColumn.HIDE_DATE_SELECTOR:
      case DefaultDateOptionsColumn.DATE_RANGE:
      default:
        break;
    }
  }, [periodOption.type]);

  const renderInputDate = () => {
    if (!periodOptions?.length) return <></>;
    switch (periodOption.type) {
      case DefaultDateOptionsColumn.HIDE_DATE_SELECTOR:
        return <></>;
      case DefaultDateOptionsColumn.DATE:
        return (
          <Input
            formControlClassName={classes.paddingLeft}
            type="date"
            value={formatDate(rangeDates?.startDate)}
            onChange={(event) => {
              const date = event.target.value;

              setFilters((prev) => ({
                ...prev,
                [DefaultDateOptionsColumn.DATE]: [date],
              }));
            }}
          />
        );
      case DefaultDateOptionsColumn.DATE_RANGE:
      default:
        return (
          <InputDateRange
            className={classes.dateRangeInput}
            pStartDate={rangeDates.startDate}
            pEndDate={rangeDates.endDate}
            onChange={(range) => {
              const startDate = range?.startDate ? formatDate(range.startDate) : undefined;
              const endDate = range?.endDate ? formatDate(range.endDate) : undefined;

              if (!startDate || !endDate) {
                handleClearDateRange();
              } else {
                setFilters(
                  (prev) =>
                    ({
                      ...prev,
                      [DefaultDateOptionsColumn.DATE_RANGE]: [startDate, endDate],
                    } as { [x: string]: string[] })
                );
              }
            }}
          />
        );
    }
  };

  const periodOptions = useMemo(() => {
    if (dateOption?.type === DefaultDateOptionsColumn.HIDE_DATE_SELECTOR) return [];
    if (overridePeriodOptions && overridePeriodOptions.length) {
      return overridePeriodOptions;
    }

    return defaultPeriodOptions;
  }, [overridePeriodOptions, defaultPeriodOptions, dateOption]);

  return (
    <div className={classes.row}>
      {dateOptions?.length && (
        <Select
          label={"Date type"}
          data-cy="select-date-type"
          name="date-type"
          placeholder={"Select"}
          value={column}
          onChange={(event: SelectChangeEvent<any>) => handleChangeDateOption(event.target.value)}
          fullWidth={false}
          disabled={dateOptions?.length === 1}
        >
          {dateOptions?.map((dateOption, index) => (
            <MenuItem key={index} value={dateOption.column}>
              {dateOption.name}
            </MenuItem>
          ))}
        </Select>
      )}
      {periodOptions?.length ? (
        <div className={dateOptions?.length ? classes.paddingLeft : ""}>
          <Select
            label={"Period"}
            data-cy="select-date-type"
            name="date-type"
            placeholder={"Select"}
            value={periodOption.column}
            onChange={(event: SelectChangeEvent<any>) =>
              handleChangePeriodOption(event.target.value)
            }
            fullWidth={false}
            disabled={periodOptions?.length === 1}
          >
            {periodOptions?.map((periodOption, index) => (
              <MenuItem key={index} value={periodOption.column}>
                {periodOption.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      ) : null}
      {renderInputDate()}
    </div>
  );
};

export default SelectDate;
